// Quando una sessione è ancora buona, e quando è finita.
//
// Nasce da un guasto raccontato così: «quando la sessione scade durante
// un'impersonazione non riesco più a entrare con SSO — Token non valido o
// scaduto». Il difetto non era nell'SSO: era che **una sessione morta
// continuava a dirsi viva**.
//
// `isAuthenticated` era l'unica domanda che il prodotto faceva, e resta `true`
// finché qualcuno non chiama logout(). Con i token scaduti in mano, la pagina
// di accesso rimbalzava chi ci arrivava dentro l'applicazione (perché «sei già
// autenticato»), dove ogni chiamata rispondeva 401 — quindi il pulsante
// «Accedi con SSO» non lo si raggiungeva nemmeno.
//
// La domanda giusta non è «hai un token» ma «con quello che hai in mano puoi
// ancora lavorare»: o l'access token è ancora valido, oppure c'è un refresh
// token vivo con cui rinnovarlo. Se nessuna delle due, la sessione è finita,
// e chi lo sa può mandare la persona a rientrare invece di chiuderla fuori.

// L'exp di un JWT in millisecondi, o null se il token manca o non si legge.
// Non verifica la firma — non è questo il mestiere del browser: serve solo a
// sapere se vale ancora la pena provarci.
export function scadenzaToken(token) {
  const payload = payloadToken(token);
  return payload?.exp ? payload.exp * 1000 : null;
}

// Il contenuto di un JWT, o null se non si legge. Serve a due domande diverse
// (quando scade, e se è un token di impersonazione), quindi sta qui una volta.
export function payloadToken(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const parte = token.split('.')[1];
    if (!parte) return null;
    return JSON.parse(atob(parte.replace(/-/g, '+').replace(/_/g, '/')));
  } catch (_) {
    return null;
  }
}

// Un token senza exp leggibile NON è considerato scaduto: potrebbe essere un
// formato che non conosciamo, e trattarlo come morto butterebbe fuori una
// sessione buona. L'unico caso in cui rispondiamo «scaduto» è quando la data
// c'è ed è passata.
export function tokenScaduto(token, adesso = Date.now(), margineMs = 0) {
  const exp = scadenzaToken(token);
  if (exp === null) return false;
  return exp <= adesso + margineMs;
}

// La sessione serve ancora a qualcosa?
//
//   - senza access token non c'è niente da usare;
//   - access token valido → sì;
//   - access token scaduto ma refresh token vivo → sì, si rinnova (è il caso
//     normale di chi torna dopo un'ora);
//   - scaduti entrambi → no: è finita, e va detto.
export function sessioneUtilizzabile(stato, adesso = Date.now()) {
  if (!stato || !stato.isAuthenticated || !stato.accessToken) return false;
  if (!tokenScaduto(stato.accessToken, adesso)) return true;
  return Boolean(stato.refreshToken) && !tokenScaduto(stato.refreshToken, adesso);
}

// Le pagine che servono a entrare. Su queste la macchina della sessione deve
// stare ferma: il rinnovo periodico, il conto alla rovescia e la finestra
// «sessione persa» appartengono a una sessione che sta lavorando, non a una
// che sta per nascere. Lasciarli accesi qui significa che i resti della
// sessione morta corrono contro l'accesso nuovo — ed è esattamente quello che
// si vedeva nei log: /auth/oidc/complete 200, e subito dopo una raffica di
// /auth/refresh 401 con il token vecchio.
export const PAGINE_DI_ACCESSO = ['/login', '/sso-callback', '/magic-link', '/onboarding'];

export function paginaDiAccesso(percorso) {
  if (!percorso) return false;
  return PAGINE_DI_ACCESSO.some((p) => percorso === p || percorso.startsWith(`${p}/`));
}

// Un'impersonazione rimasta orfana.
//
// Trovata viva nel browser di chi ha segnalato il guasto, due giorni dopo i
// fatti: `impersonating` diceva «stai visualizzando come Lorenzo Oliva»,
// mentre il token in uso era quello del SUPER_ADMIN, e la coppia da
// ripristinare con «Termina impersonazione» era scaduta il 27 alle 17:11.
// Una fascia rossa che dichiara un'identità che non è quella con cui si sta
// lavorando è peggio di un errore: è un'informazione falsa sullo schermo.
//
// Tre modi di essere orfana, tutti e tre significano «questo stato è un
// residuo, non un'impersonazione in corso»:
//   - non c'è nessun token in mano;
//   - il token in mano non è di un'impersonazione (niente `impersonator_id`):
//     il rinnovo automatico l'ha già sostituito con quello di chi impersona;
//   - non c'è più modo di tornare indietro: la coppia originale è morta.
export function impersonazioneOrfana(stato, adesso = Date.now()) {
  if (!stato || !stato.impersonating) return false;
  if (!stato.accessToken) return true;
  const payload = payloadToken(stato.accessToken);
  if (payload && !payload.impersonator_id) return true;
  return !sessioneUtilizzabile({
    isAuthenticated: true,
    accessToken: stato._originalAccessToken,
    refreshToken: stato._originalRefreshToken,
  }, adesso);
}

// Normalizza il profilo utente ricevuto da login, sblocco schermo (LockScreen),
// impersonazione o `/users/me`. Allinea `moduli_abilitati` e `moduliAbilitati`,
// preserva `phone_enabled` / `video_enabled` e i campi già caricati quando si
// aggiorna la stessa persona (evitando che `unlock()` azzeri i moduli o smonti
// il softphone WebRTC).
export function normalizzaUtente(nextUser, prevUser = null) {
  if (!nextUser || typeof nextUser !== 'object') return nextUser ?? null;
  const stessoUtente = Boolean(prevUser && nextUser.id && prevUser.id === nextUser.id);

  let moduli = [];
  if (Array.isArray(nextUser.moduli_abilitati)) {
    moduli = nextUser.moduli_abilitati;
  } else if (Array.isArray(nextUser.moduliAbilitati)) {
    moduli = nextUser.moduliAbilitati;
  } else if (stessoUtente && Array.isArray(prevUser.moduli_abilitati)) {
    moduli = prevUser.moduli_abilitati;
  } else if (stessoUtente && Array.isArray(prevUser.moduliAbilitati)) {
    moduli = prevUser.moduliAbilitati;
  }

  let phoneEnabled = false;
  if (typeof nextUser.phone_enabled === 'boolean') {
    phoneEnabled = nextUser.phone_enabled;
  } else if (typeof nextUser.phoneEnabled === 'boolean') {
    phoneEnabled = nextUser.phoneEnabled;
  } else if (stessoUtente && typeof prevUser.phone_enabled === 'boolean') {
    phoneEnabled = prevUser.phone_enabled;
  }

  let videoEnabled = false;
  if (typeof nextUser.video_enabled === 'boolean') {
    videoEnabled = nextUser.video_enabled;
  } else if (typeof nextUser.videoEnabled === 'boolean') {
    videoEnabled = nextUser.videoEnabled;
  } else if (stessoUtente && typeof prevUser.video_enabled === 'boolean') {
    videoEnabled = prevUser.video_enabled;
  }

  return {
    ...(stessoUtente ? prevUser : {}),
    ...nextUser,
    moduli_abilitati: moduli,
    moduliAbilitati: moduli,
    phone_enabled: phoneEnabled,
    video_enabled: videoEnabled,
  };
}

const AVVISI_OIDC = {
  pending: {
    variante: 'wfm-toast-info',
    ruoloAria: 'status',
    testo: 'Account creato tramite SSO. È in attesa di approvazione da parte di un amministratore prima di poter accedere.',
  },
  disabled: {
    variante: 'wfm-toast-error',
    ruoloAria: 'alert',
    testo: 'Accesso SSO non disponibile per questo account o disabilitato.',
  },
  rate_limited: {
    variante: 'wfm-toast-error',
    ruoloAria: 'alert',
    testo: 'Troppi tentativi di accesso SSO ravvicinati. Attendi qualche minuto e riprova.',
  },
  missing_email: {
    variante: 'wfm-toast-error',
    ruoloAria: 'alert',
    testo: 'Profilo SSO privo di indirizzo email. Completa il campo email sul portale SSO o contatta un amministratore.',
  },
  domain: {
    variante: 'wfm-toast-error',
    ruoloAria: 'alert',
    testo: 'Dominio email del profilo SSO non autorizzato per questo portale.',
  },
  state_expired: {
    variante: 'wfm-toast-error',
    ruoloAria: 'alert',
    testo: 'Sessione di autenticazione SSO scaduta o già utilizzata. Avvia un nuovo accesso.',
  },
  error: {
    variante: 'wfm-toast-error',
    ruoloAria: 'alert',
    testo: 'Accesso SSO non riuscito. Riprova o usa un altro metodo di accesso.',
  },
};

export function avvisoOidc(codice) {
  if (!codice || typeof codice !== 'string') return null;
  const pulito = codice.trim();
  if (!pulito) return null;
  return AVVISI_OIDC[pulito] || AVVISI_OIDC.error;
}

// I codici di passaggio SSO (/sso-callback?code=...) e i Magic Link sono
// monouso lato server (GETDEL su Redis → 200 la prima volta, 410 la seconda).
// In React 18 StrictMode (o se l'effetto si riesegue prima del redirect) il
// primo mount consuma il codice sul server ma ignora il 200 per `cancelled`,
// mentre il secondo mount riceve 410. Condividere la stessa Promise per chiave
// garantisce una sola chiamata di rete e consegna il 200 al mount attivo.
const LIMITE_RISCATTI = 32;
const riscattiMonouso = new Map();

export function riscattaUnaVolta(chiave, operazione) {
  if (!chiave) return Promise.reject(new Error('Chiave mancante'));
  if (riscattiMonouso.has(chiave)) {
    return riscattiMonouso.get(chiave);
  }
  if (riscattiMonouso.size >= LIMITE_RISCATTI) {
    const primaChiave = riscattiMonouso.keys().next().value;
    riscattiMonouso.delete(primaChiave);
  }
  const promessa = Promise.resolve()
    .then(() => operazione())
    .catch((err) => {
      riscattiMonouso.delete(chiave);
      throw err;
    });
  riscattiMonouso.set(chiave, promessa);
  return promessa;
}
